import { Button, IconButton } from '@ui/shared/Button';
import CSV from 'comma-separated-values';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import * as React from 'react';
import Dropzone, { DropzoneState } from 'react-dropzone';
import styled from 'styled-components';
import { IAthleteCSVRow, ITeam } from '../../../../../data/types';
import { Row } from '../../../../layout/Row';
import { IErrorState } from '../validation';

interface IProps {
  handleImport: (i: IAthleteCSVRow[]) => void;
  uploading?: boolean;
  selectedTeams: ITeam[];
  onError: (error: IErrorState) => void;
}

const CSVImport = (props: IProps): React.ReactElement => {
  /**
   * @description parses athletes from csv file
   * @param e
   */
  const parseFile = (e: ProgressEvent<FileReader>): void => {
    const { result } = e.target;
    const rows = [];
    const csv = new CSV(result, { header: true, cast: false });
    csv.parse();
    csv.forEach((item: Array<number | string>): void => {
      const row = reduce(
        map(
          item,
          (
            val: string | number,
            idx: number
          ): {
            [key: number]: string | number;
          } => {
            const obj = {};
            if (!isEmpty(val)) {
              obj[idx] = val;
            }

            return obj;
          }
        ),
        (
          prev: { [key: number]: string | number },
          curr: { [key: number]: string | number }
        ): { [key: number]: string | number } => {
          return { ...prev, ...curr };
        },
        {}
      );
      rows.push(row);
    });
    const filteredRows = rows.filter(
      (row: IAthleteCSVRow): boolean => !!row.name
    );
    props.handleImport(filteredRows);
  };

  /**
   * @description reads the imported file
   * @param file
   */
  const readFile = (file: File): void => {
    const reader = new FileReader();
    reader.onload = parseFile;
    reader.readAsText(file);
  };

  /**
   * @description handles file from dropzone lib
   * @param acceptedFiles
   * @param rejectedFiles
   */
  const handleFile = (acceptedFiles: File[], rejectedFiles: File[]): void => {
    if (rejectedFiles.length > 0) {
      props.onError({
        active: true,
        message: 'Only CSV files can be imported.',
      });
      return;
    }
    if (acceptedFiles.length > 0) {
      props.onError({ active: false });
      readFile(acceptedFiles[0]);
    }
  };

  return (
    <Row
      style={{
        flex: 0,
        padding: '10px',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <DownloadLink href='/import_template.csv' download='hawkin-import-template.csv'>
        <IconButton icon='download' onClick={null} title='Download Template' />
        <DownloadLabel>Download CSV Import Template</DownloadLabel>
      </DownloadLink>
      <Dropzone
        onDrop={handleFile}
        accept={[
          'text/csv',
          'application/csv',
          'application/x-csv',
          'text/comma-separated-values',
          'text/x-comma-separated-values',
          'application/vnd.ms-excel',
        ]}
      >
        {({
          getRootProps,
          getInputProps,
        }: DropzoneState): React.ReactElement => (
          <section>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <Button
                icon='upload'
                title='Import Roster'
                onClick={() => {}}
                disabled={props.selectedTeams.length === 0}
              />
            </div>
          </section>
        )}
      </Dropzone>
    </Row>
  );
};

export { CSVImport };

const DownloadLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
`;

const DownloadLabel = styled.span`
  font-size: 12px;
  color: var(--color-text);
  white-space: nowrap;
`;
